import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdminCompany,
  AdminIndustry,
  AdminSector,
  CompensationRecord,
  fetchCompanies,
  fetchIndustries,
  fetchRecords,
  fetchRecordSources,
  fetchSectors,
  formatDate,
  formatYesNo,
  recalculateRecordGradings,
} from '../../../../services/adminService';
import {
  AdminBreadcrumbsTw,
  AdminEmpty,
  AdminPageHeader,
  AdminTableWrap,
  btnLink,
  btnSecondary,
  filtersRowClass,
  inputClass,
  selectClass,
  tdClass,
} from '../adminUi';
import {
  AdminColumnPicker,
  AdminConfigurableTable,
  AdminColumnDef,
  useAdminColumnVisibility,
} from '../adminColumnVisibility';
import { recordCompensationColumns } from '../adminRecordColumns';
import { createRecordApprovedColumn } from '../RecordApprovedCell';
import RecordGradingAssignModal from './RecordGradingAssignModal';
import RecordGradingViewModal from './RecordGradingViewModal';
import { useAdminPermissions } from '../adminPermissions';
import { RECORD_SOURCE_PRESETS } from '../RecordDrawer';

function recordSourceLabel(value: string): string {
  return RECORD_SOURCE_PRESETS.find((preset) => preset.value === value)?.label ?? value;
}

export default function RecordGradingSection() {
  const { canManageStructure } = useAdminPermissions();
  const [industries, setIndustries] = useState<AdminIndustry[]>([]);
  const [sectors, setSectors] = useState<AdminSector[]>([]);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [records, setRecords] = useState<CompensationRecord[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsPage, setRecordsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [sort, setSort] = useState('created_at_desc');
  const [roleNotValidatedOnly, setRoleNotValidatedOnly] = useState(false);

  const [assignTarget, setAssignTarget] = useState<CompensationRecord | null>(null);
  const [viewTarget, setViewTarget] = useState<CompensationRecord | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState<string | null>(null);

  const handleRecordApproved = useCallback((updated: CompensationRecord) => {
    setRecords((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }, []);

  const gradingColumns = useMemo((): AdminColumnDef<CompensationRecord>[] => [
    {
      id: 'industry',
      label: 'Индустрия',
      dbField: 'industries.name',
      render: (record) => record.industry_name || '—',
    },
    {
      id: 'sector',
      label: 'Сектор',
      dbField: 'sectors.name',
      render: (record) => record.sector_name || '—',
    },
    {
      id: 'company',
      label: 'Компания',
      dbField: 'companies.name',
      render: (record) => record.company_name,
    },
    {
      id: 'last_name',
      label: 'Фамилия',
      dbField: 'compensation_records.last_name',
      render: (record) => record.last_name || '—',
    },
    {
      id: 'first_name',
      label: 'Имя',
      dbField: 'compensation_records.first_name',
      render: (record) => record.first_name || '—',
    },
    {
      id: 'middle_name',
      label: 'Отчество',
      dbField: 'compensation_records.middle_name',
      render: (record) => record.middle_name || '—',
    },
    {
      id: 'role_name',
      label: 'Должность',
      dbField: 'compensation_records.company_role_name',
      render: (record) => record.company_role_name,
    },
    {
      id: 'grade',
      label: 'Грейд',
      dbField: 'compensation_records.grade_level',
      render: (record) => record.grade_level ?? '—',
    },
    {
      id: 'grading_role',
      label: 'Роль',
      dbField: 'roles.name',
      render: (record) => record.role_name || '—',
    },
    {
      id: 'period',
      label: 'Период',
      dbField: 'compensation_records.added_date',
      render: (record) => formatDate(record.added_date),
    },
    ...recordCompensationColumns,
    {
      id: 'source',
      label: 'Источник',
      dbField: 'compensation_records.source',
      render: (record) => record.source,
    },
    {
      id: 'created',
      label: 'Добавлено',
      dbField: 'compensation_records.created_at',
      render: (record) => formatDate(record.created_at),
    },
    createRecordApprovedColumn(handleRecordApproved),
    {
      id: 'role_validated',
      label: 'Грейд подтвержден',
      dbField: 'compensation_records.is_role_validated',
      render: (record) => formatYesNo(record.is_role_validated),
    },
    {
      id: 'actions',
      label: 'Действия',
      hideable: false,
      cellClassName: `${tdClass} whitespace-nowrap`,
      render: (record) => (
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          <button type="button" className={btnLink} onClick={() => setViewTarget(record)}>
            Посмотреть
          </button>
          <button type="button" className={btnLink} onClick={() => setAssignTarget(record)}>
            {record.is_role_validated ? 'Изменить грейд' : 'Назначить грейд'}
          </button>
        </div>
      ),
    },
  ], [handleRecordApproved]);

  const gradingColVis = useAdminColumnVisibility('record-grading', gradingColumns);

  const loadRecords = useCallback(async () => {
    const res = await fetchRecords({
      search: search.trim() || undefined,
      industryId: industryFilter ? Number(industryFilter) : undefined,
      sectorId: sectorFilter ? Number(sectorFilter) : undefined,
      companyId: companyFilter ? Number(companyFilter) : undefined,
      source: sourceFilter || undefined,
      sort,
      page: recordsPage,
      limit: 25,
      roleNotValidated: roleNotValidatedOnly || undefined,
    });
    setRecords(res?.data || []);
    setRecordsTotal(res?.total || 0);
  }, [search, industryFilter, sectorFilter, companyFilter, sourceFilter, sort, recordsPage, roleNotValidatedOnly]);

  const reloadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      if (industryFilter) await fetchSectors(Number(industryFilter)).then(setSectors);
      if (sectorFilter || industryFilter) {
        await fetchCompanies({
          industryId: industryFilter ? Number(industryFilter) : undefined,
          sectorId: sectorFilter ? Number(sectorFilter) : undefined,
        }).then(setCompanies);
      }
      await loadRecords();
    } finally {
      setRefreshing(false);
    }
  }, [industryFilter, sectorFilter, loadRecords]);

  const handleRecalculateGradings = useCallback(async () => {
    const confirmed = window.confirm(
      'Пересчитать грейды всех записей по текущей методологии?\n\n'
      + 'Будут обновлены суммы баллов и роли для записей, прошедших полное грейдирование (с ответами на вопросы).',
    );
    if (!confirmed) return;

    setRecalculating(true);
    setRecalcMessage(null);
    try {
      const result = await recalculateRecordGradings({ dryRun: false });
      setRecalcMessage(
        `Готово: обработано ${result.processed}, обновлено ${result.updated}, `
        + `изменено ролей ${result.roleChanged}`
        + (result.noRoleMatch ? `, без подходящей роли: ${result.noRoleMatch}` : ''),
      );
      await reloadAll();
    } catch (e) {
      setRecalcMessage(e instanceof Error ? e.message : 'Ошибка пересчёта');
    } finally {
      setRecalculating(false);
    }
  }, [reloadAll]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const ind = await fetchIndustries();
        if (cancelled) return;
        setIndustries(ind);
        const sources = await fetchRecordSources();
        if (cancelled) return;
        setSourceOptions(sources);
        if (industryFilter) {
          const sec = await fetchSectors(Number(industryFilter));
          if (cancelled) return;
          setSectors(sec);
        }
        if (sectorFilter || industryFilter) {
          const comp = await fetchCompanies({
            industryId: industryFilter ? Number(industryFilter) : undefined,
            sectorId: sectorFilter ? Number(sectorFilter) : undefined,
          });
          if (cancelled) return;
          setCompanies(comp);
        }
        await loadRecords();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (loading) return;
    reloadAll();
  }, [search, industryFilter, sectorFilter, companyFilter, sourceFilter, sort, recordsPage, roleNotValidatedOnly]);

  if (loading) return <AdminEmpty>Загрузка…</AdminEmpty>;

  return (
    <>
      <AdminBreadcrumbsTw items={[{ label: 'Админ-панель' }, { label: 'Грейдирование' }]} />
      <AdminPageHeader
        title="Грейдирование"
        subtitle="Назначение ролей и грейдов сотрудникам по записям компенсации"
        actions={canManageStructure ? (
          <button
            type="button"
            className={btnSecondary}
            disabled={recalculating}
            onClick={handleRecalculateGradings}
          >
            {recalculating ? 'Пересчёт…' : 'Пересчитать грейды'}
          </button>
        ) : undefined}
      />

      {recalcMessage && (
        <p className="mb-4 text-sm text-slate-600">{recalcMessage}</p>
      )}

      <div className={filtersRowClass}>
        <input
          className={inputClass}
          placeholder="Глобальный поиск"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setRecordsPage(1); }}
        />
        <select
          className={selectClass}
          value={industryFilter}
          onChange={(e) => {
            setIndustryFilter(e.target.value);
            setSectorFilter('');
            setCompanyFilter('');
            setRecordsPage(1);
          }}
        >
          <option value="">Все индустрии</option>
          {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select
          className={selectClass}
          value={sectorFilter}
          onChange={(e) => {
            setSectorFilter(e.target.value);
            setCompanyFilter('');
            setRecordsPage(1);
          }}
          disabled={!industryFilter}
        >
          <option value="">Все сектора</option>
          {sectors.filter((s) => String(s.industry_id) === industryFilter).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select
          className={selectClass}
          value={companyFilter}
          onChange={(e) => { setCompanyFilter(e.target.value); setRecordsPage(1); }}
        >
          <option value="">Все компании</option>
          {companies.map((c) => <option key={`${c.id}-${c.sector_id}`} value={c.id}>{c.name}</option>)}
        </select>
        <select
          className={selectClass}
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setRecordsPage(1); }}
        >
          <option value="">Все источники</option>
          {sourceOptions.map((source) => (
            <option key={source} value={source}>{recordSourceLabel(source)}</option>
          ))}
        </select>
        <select className={selectClass} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="created_at_desc">Сначала новые</option>
          <option value="added_date_desc">По периоду</option>
        </select>
        <button
          type="button"
          className={`${btnSecondary} whitespace-nowrap ${roleNotValidatedOnly ? '!border-slate-900 !bg-slate-900 !text-white' : ''}`}
          onClick={() => { setRoleNotValidatedOnly((v) => !v); setRecordsPage(1); }}
        >
          Грейд не подтверждён
        </button>
        <AdminColumnPicker
          hideableColumns={gradingColVis.hideableColumns}
          hiddenIds={gradingColVis.hiddenIds}
          onToggle={gradingColVis.toggleColumn}
          onShowAll={gradingColVis.showAllColumns}
        />
      </div>

      <AdminTableWrap refreshing={refreshing}>
        <AdminConfigurableTable
          columns={gradingColVis.visibleColumns}
          rows={records}
          rowKey={(record) => record.id}
          minWidth={1180}
        />
      </AdminTableWrap>

      {records.length === 0 && (
        <p className="mt-4 text-sm text-slate-500">Записи не найдены.</p>
      )}

      {recordsTotal > 25 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-dc-muted">
          <span>Всего: {recordsTotal}</span>
          <div className="flex gap-2">
            <button
              type="button"
              className={btnSecondary}
              disabled={recordsPage <= 1}
              onClick={() => setRecordsPage((p) => p - 1)}
            >
              Назад
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={recordsPage * 25 >= recordsTotal}
              onClick={() => setRecordsPage((p) => p + 1)}
            >
              Вперёд
            </button>
          </div>
        </div>
      )}

      {assignTarget && (
        <RecordGradingAssignModal
          record={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={reloadAll}
        />
      )}

      {viewTarget && (
        <RecordGradingViewModal
          record={viewTarget}
          onClose={() => setViewTarget(null)}
        />
      )}
    </>
  );
}
